import type { ReactElement } from 'react'
import React, { useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'

import PhosphorIcon from '@ui-kit/components/PhosphorIcon'
import MenuContainerComponent from '@ui-kit/components/formComponents/Select/components/MenuContainer'
import OptionsContainerComponent from '@ui-kit/components/formComponents/Select/components/OptionsContainer'
import { getOptionIdentifier } from '@ui-kit/components/formComponents/Select/utils'
import TextFieldContainer from '@ui-kit/components/formComponents/TextFieldContainer/index.web'
import * as textFieldContainerStyles from '@ui-kit/components/formComponents/TextFieldContainer/styles'
import { FORM_ELEMENT } from '@ui-kit/constants'
import { body2 } from '@ui-kit/core/typography'
import { isFieldValueSet } from '@ui-kit/utils/formComponentUtils'

import * as styles from './styles'
import type { OptionValue, WebProps } from './types'

const Select = (props: WebProps): ReactElement => {
  const [isFocused, setIsFocused] = useState(props.autoFocus ?? false)
  const [filterValue, setFilterValue] = useState('')
  const [activeMenuOption, setActiveMenuOption] = useState('')
  const fieldRef = useRef<HTMLDivElement>(null)
  const isFilled = isFieldValueSet(props.value)

  const { disabled } = props

  const optionsWithFallback = props.fallbackOptions
    ? props.options.concat(props.fallbackOptions)
    : props.options

  const currentOptionLabel = optionsWithFallback.find((o) => o.value === props.value)?.label || ''

  const shouldRenderInputField = props.useFilterInput && isFocused

  const onSelectOptionHandler = (newValue: OptionValue) => {
    if (disabled || newValue === props.value) {
      return
    }

    if (typeof props.onValueChange === 'function') {
      props.onValueChange(newValue)
    }

    if (props.useFilterInput) {
      setFilterValue('')
    }
  }

  const onChangeFilterInputValue = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue(event.target.value)
    if (props.onChangeFilterInputValue) {
      await props.onChangeFilterInputValue(event.target.value)
      return
    }

    setActiveMenuOption(getOptionIdentifier(event.target.value, props.name))
  }

  const filteredOptions =
    !props.useFilterInput || !filterValue || props.onChangeFilterInputValue
      ? props.options
      : props.options.filter((option) =>
          typeof props.filterFunction === 'function'
            ? props.filterFunction(option)
            : option.label.toLowerCase().includes(filterValue.toLowerCase())
        )

  const optionsToRender =
    props.fallbackOptions && !filteredOptions.length ? props.fallbackOptions : filteredOptions

  return (
    <MenuContainerComponent
      name={props.name}
      onSelectOptionHandler={onSelectOptionHandler}
      options={optionsToRender}
      setActiveMenuOption={setActiveMenuOption}
      activeMenuOption={activeMenuOption}
      isFocused={isFocused}
      setIsFocused={setIsFocused}
      value={props.value}
      onFocus={props.onFocus}
      onBlur={props.onBlur}
      disabled={props.disabled}
      containerRef={fieldRef}
    >
      <TextFieldContainer
        isFilled={isFilled}
        assistiveText={props.assistiveText}
        disabled={disabled}
        errorMessage={props.errorMessage}
        label={props.label}
        styleType={props.styleType}
        isFocused={isFocused}
        name={props.name}
        status={props.status}
      >
        <div className={styles.fieldContainer()}>
          {shouldRenderInputField && (
            <input
              className={textFieldContainerStyles.textInput()}
              onChange={onChangeFilterInputValue}
              onFocus={(e) => e.target.select()}
              value={filterValue}
              autoFocus
              id={`filter-input-${props.name}`}
            />
          )}
          {!shouldRenderInputField && (
            <span
              className={body2({
                color: props.errorMessage ? FORM_ELEMENT.errorColorName : undefined,
                className: 'line-clamp-1',
              })}
            >
              {props.getCustomDisplayValue
                ? props.getCustomDisplayValue(props.value)
                : currentOptionLabel}
            </span>
          )}
          {props.hideIcon ? null : (
            <div
              className={twMerge(
                'absolute right-0 top-0 flex h-full items-center px-2 transition',
                isFocused && 'rotate-180'
              )}
            >
              <PhosphorIcon name="CaretDown" size={24} />
            </div>
          )}

          {isFocused && (
            <OptionsContainerComponent
              activeMenuOption={activeMenuOption}
              name={props.name}
              onSelectOptionHandler={onSelectOptionHandler}
              options={optionsToRender}
              setActiveMenuOption={setActiveMenuOption}
              anchorRef={fieldRef}
            />
          )}
        </div>
      </TextFieldContainer>
    </MenuContainerComponent>
  )
}

export default Select
